import {
  mapFacilityTypes,
  mapFollowUpMethods,
  mapRecordTypes,
} from './mapFormHelper';
import {
  isDeeplyEmptyObject,
  isObjectEffectivelyEmpty,
  isObjectEmpty,
  validateAndClearSingleItemArray,
} from './validationHelpers';

const mapEntityFormDataToEntity = (mainInfoForm: any, entityData: any) => {
  const values = mainInfoForm.values;
  const custodianDetails = values.custodianDetails;

  const validatedPortalUrls = validateAndClearSingleItemArray(
    values.portalUrls,
    (portal) => isObjectEffectivelyEmpty(portal, { treatFalseAsEmpty: true }),
  );
  const validatedSource = validateAndClearSingleItemArray(
    values.externalSourceMappings
  );
  const validatedEmails = validateAndClearSingleItemArray(
    values.emails,
    (item) => isObjectEffectivelyEmpty(item, { treatFalseAsEmpty: true }),
  );
  const validatedContacts = validateAndClearSingleItemArray(
    values.contacts,
    (item) => isObjectEffectivelyEmpty(item, { treatFalseAsEmpty: true }),
  );
  const validatedDepartments = validateAndClearSingleItemArray(
    values.departmentAndPhysicians,
  );

  const newEntityDataVal = {
    ...entityData,
    isFacility: values.isFacility,
    isCustodian: values.isCustodian,
    notes: values.notes,
    name: values.name,

    facilityTypes:
      values.facilityTypes?.length > 0
        ? mapFacilityTypes(values.facilityTypes, entityData.facilityTypes)
        : null,

    facilityTypeNotes: values.facilityTypeNotes,
    departmentAndPhysicians: validatedDepartments,

    portalUrls: validatedPortalUrls
      ? validatedPortalUrls.map((portal: any) => ({
          portalUrl: portal.portalUrl,
          portalTypeId: portal.portalTypeId,
          portalNotes: portal.portalNotes,
        }))
      : null,
    externalSourceMappings: validatedSource ?
     validatedSource?.map((m: any) => ({
          mappedId: m.mappedId,
          sourceTypeId: m.sourceTypeId,
          notes: m.notes,
        }))
      : null,

    emailAddresses: validatedEmails
      ? validatedEmails.map((emailObj: any) => ({
          id: emailObj.id,
          email: emailObj.email,
        }))
      : null,

    phoneNumbers: values.phones?.map((p: any) => ({
      id: p.id,
      isPrimary: p.isPrimary,
      phoneNumber: p.phoneNumber,
      phoneTypeId: p.phoneTypeId,
    })),

    contacts: validatedContacts,

    address: {
      addressDetails: values.addressInfo?.addressDetails,
      city: values.addressInfo?.city,
      postCode: values.addressInfo?.postCode,
      stateId: values.addressInfo?.stateId,
      timeZoneId: values.addressInfo?.timeZoneId,
    },

    mailingAddress:
      isObjectEmpty(values.mailingAddress) === false
        ? {
            addressDetails: values.mailingAddress?.addressDetails,
            city: values.mailingAddress?.city,
            postCode: values.mailingAddress?.postCode,
            stateId: values.mailingAddress?.stateId,
            timeZoneId: values.mailingAddress?.timeZoneId,
          }
        : null,

    custodianDetails:
      isDeeplyEmptyObject(custodianDetails) === false && values.isCustodian
        ? {
            recordTypes:
              custodianDetails.recordTypes?.length > 0
                ? mapRecordTypes(
                    custodianDetails?.recordTypes,
                    entityData?.custodianDetails?.recordTypes,
                  )
                : [],

            recordTypeNotes: custodianDetails?.recordTypeNotes,
            orderTypeName: custodianDetails?.orderTypeName,
            orderTypeId: custodianDetails?.orderTypeId,
            orderTypeNotes: custodianDetails?.orderTypeNotes,

            followUpMethods:
              custodianDetails?.followUpMethods?.length > 0
                ? mapFollowUpMethods(
                    custodianDetails?.followUpMethods,
                    entityData?.custodianDetails?.followUpMethods,
                  )
                : [],

            entityMetric: {
              averageProcessingTime:
                custodianDetails.entityMetric?.averageProcessingTime,
              lastRecordRetrievedDate:
                custodianDetails.entityMetric?.lastRecordRetrievedDate,
            },

            daysAvailable:
              custodianDetails.daysAvailable?.map((day: any) => ({
                dayId: day.dayId ?? day.dayOfWeek,
                timeZoneId: day.timeZoneId,
                serveHours: {
                  from: day.serveHours?.from,
                  to: day.serveHours?.to,
                },
                pickupAndCopyHours: {
                  from: day.pickupAndCopyHours?.from,
                  to: day.pickupAndCopyHours?.to,
                },
                bestTimeToCall: {
                  from: day.bestTimeToCall?.from,
                  to: day.bestTimeToCall?.to,
                },
              })) ?? [],

            serveMethods:
              custodianDetails.serveMethods?.map(
                (m: { serveMethodId: any }) => ({
                  serveMethodId: m.serveMethodId,
                }),
              ) ?? [],

            roiMethods:
              custodianDetails.roiMethods?.map((m: { serveMethodId: any }) => ({
                serveMethodId: m.serveMethodId,
              })) ?? [],

            serveMethodNotes: custodianDetails.serveMethodNotes ?? '',
            roiMethodNotes: custodianDetails.roiMethodNotes ?? '',

            servePreferences: custodianDetails.servePreferences
              ? {
                  id: entityData?.custodianDetails?.servePreferences?.id ?? 0,
                  isPatientAddress:
                    custodianDetails.servePreferences.isPatientAddress,
                  isPatientAccountNumber:
                    custodianDetails.servePreferences.isPatientAccountNumber,
                  isPatientFullSSNNumber:
                    custodianDetails.servePreferences.isPatientFullSSNNumber,
                  isPatientInitialSensitiveInformation:
                    custodianDetails.servePreferences
                      .isPatientInitialSensitiveInformation,
                  isElectronicSignatureAccepted:
                    custodianDetails.servePreferences
                      .isElectronicSignatureAccepted,
                  isRequiredDateOfLoss:
                    custodianDetails.servePreferences.isRequiredDateOfLoss,
                  isClaimNumber:
                    custodianDetails.servePreferences.isClaimNumber,
                  isAuthorizationWithSubpoenaRequired:
                    custodianDetails.servePreferences
                      .isAuthorizationWithSubpoenaRequired,
                  isPOAForSubpoena:
                    custodianDetails.servePreferences.isPOAForSubpoena,
                  isHIPPAAuthorizationOriginal:
                    custodianDetails.servePreferences
                      .isHIPPAAuthorizationOriginal,
                  isFacilitySpecificAuthorization:
                    custodianDetails.servePreferences
                      .isFacilitySpecificAuthorization,
                  facilitySpecificAuthorizationNotes:
                    custodianDetails.servePreferences
                      .facilitySpecificAuthorizationNotes,
                  isAcceptLOR: custodianDetails.servePreferences.isAcceptLOR,
                  acceptLORNotes:
                    custodianDetails.servePreferences.acceptLORNotes,
                  isSOAForSubpoena:
                    custodianDetails.servePreferences.isSOAForSubpoena,
                  isCopyService:
                    custodianDetails.servePreferences.isCopyService,
                  copyServiceNotes:
                    custodianDetails.servePreferences.copyServiceNotes,
                  isAdditionalFacilitySpecificDocumentation:
                    custodianDetails.servePreferences
                      .isAdditionalFacilitySpecificDocumentation,
                  additionalFacilitySpecificDocumentationNotes:
                    custodianDetails.servePreferences
                      .additionalFacilitySpecificDocumentationNotes,
                  isRequiresSpecificDatesOfService:
                    custodianDetails.servePreferences
                      .isRequiresSpecificDatesOfService,
                  isRequiresDOB:
                    custodianDetails.servePreferences.isRequiresDOB,
                  isRequiresThirdPartyListedOnAuthSubpoena:
                    custodianDetails.servePreferences
                      .isRequiresThirdPartyListedOnAuthSubpoena,
                  isRequiresDeathCertificate:
                    custodianDetails.servePreferences
                      .isRequiresDeathCertificate,
                  isRequiresEstateLetter:
                    custodianDetails.servePreferences.isRequiresEstateLetter,
                  isRequiresCertificationOfNonObjection:
                    custodianDetails.servePreferences
                      .isRequiresCertificationOfNonObjection,
                  isRequiresCertificationOfNotice:
                    custodianDetails.servePreferences
                      .isRequiresCertificationOfNotice,
                  isMultipleStatusFollowUps:
                    custodianDetails.servePreferences.isMultipleStatusFollowUps,
                  isRequiresRecordTypeAndScopeInAuth:
                    custodianDetails.servePreferences
                      .isRequiresRecordTypeAndScopeInAuth,
                }
              : null,
            files:
              custodianDetails?.servePreferences?.files?.length > 0
                ? custodianDetails?.servePreferences?.files?.map((f: any) => ({
                    id: f.id,
                    fileName: f.fileName,
                    fileTypeId: f.fileTypeId,
                    permanentBlobUrl: f.permanentBlobUrl,
                    fileTypeName: f.fileTypeName,
                    fileSize: f.fileSize,
                  }))
                : [],

            billingInformation: custodianDetails.billingInformation
              ? {
                  id: entityData?.custodianDetails?.billingInformation?.id ?? 0,
                  payeeName: custodianDetails.billingInformation?.payeeName,
                  payeeAddress:
                    custodianDetails.billingInformation?.payeeAddress,
                  payeeContact:
                    custodianDetails.billingInformation?.payeeContact,
                  payeeCity: custodianDetails.billingInformation?.payeeCity,
                  payeePostCode:
                    custodianDetails.billingInformation?.payeePostCode,
                  payeeStateId:
                    custodianDetails.billingInformation?.payeeStateId,
                  processingFeesAuthorization:
                    custodianDetails.billingInformation
                      ?.processingFeesAuthorization,
                  processingFeesSubpoena:
                    custodianDetails.billingInformation?.processingFeesSubpoena,
                  invoiceBeforeRecords:
                    custodianDetails.billingInformation?.invoiceBeforeRecords,
                  billingInfoInvoicingPaymentMethods:
                    custodianDetails.billingInformation
                      ?.invoicePaymentMethods ??
                    [],
                  billingInfoProcessingFeesMethods:
                    custodianDetails.billingInformation
                      ?.processingFeesMethods ??
                    [],
                  initialProcessingFee:
                    custodianDetails.billingInformation?.initialProcessingFee,
                  initialProcessingFeeNotes:
                    custodianDetails.billingInformation
                      ?.initialProcessingFeeNotes,
                }
              : null,
          }
        : null,
  };

  return newEntityDataVal;
};

export default mapEntityFormDataToEntity;
